import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title,
  Text,
  Stack,
  Paper,
  Table,
  Button,
  Group,
  Box,
  Pagination,
  ActionIcon,
  Tooltip,
  Select,
  Loader,
  Alert,
  Checkbox,
  Anchor,
  SimpleGrid,
  Grid,
  ThemeIcon,
  Skeleton,
  Collapse
} from '@mantine/core';
import {
  IconRefresh,
  IconCheck,
  IconX,
  IconAlertCircle,
  IconEye,
  IconFilter,
  IconFilterOff,
  IconCalendar,
  IconTrendingUp,
  IconChecks,
  IconClock,
  IconChevronDown,
  IconChevronUp,
  IconLock,
  IconArrowRight
} from '@tabler/icons-react';
import { DatePickerInput } from '@mantine/dates';
import { useAuth } from '../../hooks/useAuth';
import { useMentionsQuery, useDismissMention } from '../../../application/queries/mentionQueries';
import { useMentionFilters, MentionFilters } from '../../hooks/useMentionFilters';
import { StatusBadge } from '../../components/domain/StatusBadge';
import { SubredditBadge } from '../../components/domain/SubredditBadge';
import { ConfidenceIndicator } from '../../components/domain/ConfidenceIndicator';
import { MentionDetailModal } from '../../components/domain/MentionDetailModal';
import { ConfirmMentionModal } from '../../components/domain/ConfirmMentionModal';
import { BulkConfirmModal } from '../../components/domain/BulkConfirmModal';
import { CustomerMentionCard } from '../../components/domain/CustomerMentionCard';
import { ClaimStatusBanner } from '../../components/domain/ClaimStatusBanner';
import { PaymentRequiredBanner } from '../../components/domain/PaymentRequiredBanner';
import { PendingBusinessSetupBanner } from '../../components/domain/PendingBusinessSetupBanner';
import { RedditMentionDto } from '../../../domain/entities/RedditMention';
import { appendRedditContext } from '@/utils/redditUtils';
import { useMyBusinessClaims } from '../../../application/queries/businessClaimQueries';
import { useHasSubscriptionAccess } from '../../../application/queries/subscriptionQueries';
import { useOnboardingStatusQuery } from '../../../application/queries/onboardingQueries';
import { useBusinessStore } from '../../../application/store/businessStore';
import { CUSTOMER_ROUTES } from '../../routing/routes';
import type { BusinessClaimDto } from '../../../domain/entities/BusinessClaim';

/**
 * MentionsPage - Core mention list with filters and actions
 *
 * Features:
 * - Paginated list of mentions using React Query
 * - Status filter (Pending/Confirmed/Dismissed/All)
 * - Confirm and dismiss actions
 * - URL-based filter persistence
 *
 * Session 004: Core list view implementation
 * SESSION_204: Added card-based display for business owners
 * Sprint 037 Session 9: Enhanced for business owners with welcome banner, stats, and status banners
 *                       Now serves as the primary landing page for business owners (replaces Dashboard)
 */
export const MentionsPage: React.FC = (): React.ReactElement => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'Admin';
  const [filters, setFilters] = useMentionFilters();
  const [showStats, setShowStats] = useState<boolean>(true);

  // Sprint 037 Session 9: Business store for business name
  const { businesses, fetchBusinesses } = useBusinessStore();

  // Sprint 037 Session 9: Fetch business claims for status banners
  const { data: claimsData } = useMyBusinessClaims({ enabled: !isAdmin });

  // Sprint 037 Session 9: Check subscription access for feature gating
  const { hasAccess, isLoading: isAccessLoading } = useHasSubscriptionAccess(
    !isAdmin && !!user?.businessId
  );

  // Sprint 037 Session 9: Get onboarding status to check for pending business claims
  const { data: onboardingStatus } = useOnboardingStatusQuery(!isAdmin);
  const pendingBusinessClaim = onboardingStatus?.pendingBusinessClaim;

  // Modal state management
  const [selectedMention, setSelectedMention] = useState<RedditMentionDto | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState<boolean>(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false);
  const [bulkConfirmModalOpen, setBulkConfirmModalOpen] = useState<boolean>(false);

  // Bulk selection state
  const [selectedMentionIds, setSelectedMentionIds] = useState<Set<string>>(new Set());

  // Fetch mentions with React Query
  const {
    data: mentionsResponse,
    isLoading,
    error,
    refetch
  } = useMentionsQuery(
    user?.role ?? 'BusinessOwner',
    user?.businessId ?? null,
    {
      pageNumber: filters.pageNumber,
      pageSize: filters.pageSize,
      status: filters.status,
      subreddit: filters.subreddit,
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
      sortBy: filters.sortBy,
      sortDescending: filters.sortDescending
    }
  );

  // Mutations
  const dismissMutation = useDismissMention();

  // Extract mentions and pagination from response BEFORE callbacks use them
  // Handle union type: PaginatedResult has 'items', MappedBusinessMentionsResponse has 'mentions'
  const mentions = mentionsResponse
    ? 'items' in mentionsResponse
      ? mentionsResponse.items
      : mentionsResponse.mentions
    : [];
  const totalPages = mentionsResponse?.totalPages ?? 1;
  const totalCount = mentionsResponse?.totalCount ?? 0;

  // Sprint 037 Session 9: Fetch businesses on mount for business owners
  useEffect(() => {
    if (!isAdmin && user?.businessId) {
      void fetchBusinesses();
    }
  }, [isAdmin, user?.businessId, fetchBusinesses]);

  /**
   * Sprint 037 Session 9: Get the most relevant claim to display in banner
   * Priority: PendingAdminReview > Rejected (most recent)
   */
  const activeClaim = useMemo((): BusinessClaimDto | null => {
    if (isAdmin || !claimsData?.claims?.length) {
      return null;
    }

    // First, check for any pending admin review claims
    const pendingClaim = claimsData.claims.find(
      (claim) => claim.status === 'PendingAdminReview'
    );
    if (pendingClaim) {
      return pendingClaim;
    }

    // Then check for any recently rejected claims (within last 30 days)
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const recentRejectedClaim = claimsData.claims.find((claim) => {
      if (claim.status !== 'Rejected') return false;
      if (!claim.reviewedAt) return true; // Show if no review date
      const reviewDate = new Date(claim.reviewedAt);
      return reviewDate >= thirtyDaysAgo;
    });

    return recentRejectedClaim ?? null;
  }, [isAdmin, claimsData]);

  /**
   * Sprint 037 Session 9: Get the user's business name for display
   */
  const businessName = useMemo((): string => {
    // Check business store first
    if (businesses.length > 0 && user?.businessId) {
      const userBusiness = businesses.find((b) => b.id === user.businessId);
      if (userBusiness) {
        return userBusiness.name;
      }
    }
    // Fallback to claim data
    if (claimsData?.claims?.length) {
      const approvedClaim = claimsData.claims.find((c) => c.status === 'Approved');
      if (approvedClaim) {
        return approvedClaim.businessName ?? approvedClaim.requestedBusinessName ?? 'Your business';
      }
    }
    return 'Your business';
  }, [businesses, user?.businessId, claimsData]);

  /**
   * Sprint 037 Session 9: Determine if payment required banner should be shown
   */
  const showPaymentRequired = useMemo((): boolean => {
    if (isAdmin) return false;
    if (!user?.businessId) return false;
    if (hasAccess) return false;
    if (isAccessLoading) return false;
    if (activeClaim?.status === 'PendingAdminReview') return false;
    return true;
  }, [isAdmin, user?.businessId, hasAccess, isAccessLoading, activeClaim]);

  /**
   * Sprint 037 Session 9: Calculate statistics for business owners
   */
  const stats = useMemo(() => {
    if (isAdmin) {
      return null;
    }

    const mentionsList = mentions ?? [];
    const totalMentions = mentionsList.length;
    const pendingVerification = mentionsList.filter((m) => m.matchStatus === 'Matched').length;
    const verifiedMentions = mentionsList.filter((m) => m.matchStatus === 'Verified').length;

    // Calculate average confidence score
    const avgConfidence =
      mentionsList.length > 0
        ? mentionsList.reduce((sum, m) => sum + (m.matchConfidenceScore ?? 0), 0) / mentionsList.length
        : 0;

    return {
      totalMentions,
      pendingVerification,
      verifiedMentions,
      avgConfidence: Math.round(avgConfidence * 100)
    };
  }, [isAdmin, mentions]);

  /**
   * Sprint 037 Session 9: Navigate to billing page to complete payment
   */
  const handleCompletePayment = useCallback((): void => {
    navigate(CUSTOMER_ROUTES.BILLING);
  }, [navigate]);

  /**
   * Sprint 037 Session 9: Toggle stats visibility
   */
  const handleToggleStats = useCallback((): void => {
    setShowStats((prev) => !prev);
  }, []);

  /**
   * Handle page change
   */
  const handlePageChange = useCallback(
    (page: number): void => {
      setFilters({ pageNumber: page });
    },
    [setFilters]
  );

  /**
   * Handle status filter change
   * SESSION_037_S7: 'All' is now stored explicitly, undefined defaults to 'Pending'
   */
  const handleStatusChange = useCallback(
    (value: string | null): void => {
      setFilters({
        status: (value as MentionFilters['status']) ?? 'All',
        pageNumber: 1 // Reset to first page on filter change
      });
    },
    [setFilters]
  );

  /**
   * Handle subreddit filter change
   */
  const handleSubredditChange = useCallback(
    (value: string | null): void => {
      setFilters({
        subreddit: value === 'All' ? undefined : (value ?? undefined),
        pageNumber: 1 // Reset to first page on filter change
      });
    },
    [setFilters]
  );

  /**
   * Handle date range change
   */
  const handleDateRangeChange = useCallback(
    (dates: [Date | null, Date | null] | Date | null): void => {
      // Handle array (range) or single date or null
      if (Array.isArray(dates)) {
        const [from, to] = dates;
        setFilters({
          dateFrom: from ? from.toISOString() : undefined,
          dateTo: to ? to.toISOString() : undefined,
          pageNumber: 1 // Reset to first page on filter change
        });
      }
    },
    [setFilters]
  );

  /**
   * Handle clear all filters
   * SESSION_037_S7: Resets to default state (Pending status, sorted by newest)
   */
  const handleClearFilters = useCallback((): void => {
    setFilters({
      status: 'Pending',
      subreddit: undefined,
      dateFrom: undefined,
      dateTo: undefined,
      pageNumber: 1,
      sortBy: 'CreatedAt',
      sortDescending: true
    });
  }, [setFilters]);

  /**
   * Handle view details - opens detail modal
   */
  const handleViewDetails = useCallback((mention: RedditMentionDto): void => {
    setSelectedMention(mention);
    setDetailModalOpen(true);
  }, []);

  /**
   * Handle open confirm modal
   */
  const handleOpenConfirmModal = useCallback((mention: RedditMentionDto): void => {
    setSelectedMention(mention);
    setConfirmModalOpen(true);
  }, []);

  /**
   * Handle close detail modal
   */
  const handleCloseDetailModal = useCallback((): void => {
    setDetailModalOpen(false);
    setSelectedMention(null);
  }, []);

  /**
   * Handle close confirm modal
   */
  const handleCloseConfirmModal = useCallback((): void => {
    setConfirmModalOpen(false);
    setSelectedMention(null);
  }, []);

  /**
   * Handle dismiss mention - direct action (no modal needed)
   */
  const handleDismiss = useCallback(
    async (mentionId: string): Promise<void> => {
      await dismissMutation.mutateAsync({ mentionId });
    },
    [dismissMutation]
  );

  /**
   * Handle refresh
   */
  const handleRefresh = useCallback((): void => {
    void refetch();
  }, [refetch]);

  /**
   * Format date to readable string
   */
  const formatDate = useCallback((dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }, []);

  /**
   * Handle selecting a single mention
   */
  const handleSelectOne = useCallback((mentionId: string): void => {
    setSelectedMentionIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(mentionId)) {
        newSet.delete(mentionId);
      } else {
        newSet.add(mentionId);
      }
      return newSet;
    });
  }, []);

  /**
   * Handle selecting all mentions on current page
   */
  const handleSelectAll = useCallback((checked: boolean): void => {
    if (checked) {
      // Select all selectable mentions on current page (pending only for bulk actions)
      const selectableMentions = mentions.filter(
        (m) => m.matchStatus !== 'Verified' && m.matchStatus !== 'Rejected'
      );
      setSelectedMentionIds(new Set(selectableMentions.map((m) => m.id)));
    } else {
      setSelectedMentionIds(new Set());
    }
  }, [mentions]);

  /**
   * Handle clearing selection
   */
  const handleClearSelection = useCallback((): void => {
    setSelectedMentionIds(new Set());
  }, []);

  /**
   * Handle bulk dismiss
   */
  const handleBulkDismiss = useCallback(async (): Promise<void> => {
    const selectedIds = Array.from(selectedMentionIds);

    // Show confirmation dialog
    const confirmed = window.confirm(
      `Are you sure you want to dismiss ${selectedIds.length} mention${selectedIds.length !== 1 ? 's' : ''}?`
    );

    if (!confirmed) return;

    let successCount = 0;
    let errorCount = 0;

    // Execute dismissals sequentially
    for (const mentionId of selectedIds) {
      try {
        await dismissMutation.mutateAsync({ mentionId });
        successCount++;
      } catch (error) {
        console.error(`Failed to dismiss mention ${mentionId}:`, error);
        errorCount++;
      }
    }

    // Clear selection
    setSelectedMentionIds(new Set());

    // Show result notification (using console for now, can add toast later)
    if (errorCount === 0) {
      console.log(`Success: ${successCount} mention${successCount !== 1 ? 's' : ''} dismissed`);
    } else {
      console.warn(`Partial success: Dismissed ${successCount}, Failed ${errorCount}`);
    }
  }, [selectedMentionIds, dismissMutation]);

  /**
   * Handle open bulk confirm modal
   */
  const handleOpenBulkConfirmModal = useCallback((): void => {
    setBulkConfirmModalOpen(true);
  }, []);

  /**
   * Handle close bulk confirm modal
   */
  const handleCloseBulkConfirmModal = useCallback((): void => {
    setBulkConfirmModalOpen(false);
    // Clear selection after modal closes
    setSelectedMentionIds(new Set());
  }, []);

  /**
   * Clear selection when page or filters change
   */
  useEffect((): void => {
    setSelectedMentionIds(new Set());
  }, [filters.pageNumber, filters.status, filters.subreddit, filters.dateFrom, filters.dateTo]);

  /**
   * Extract unique subreddits from current mentions
   */
  const uniqueSubreddits = useMemo((): string[] => {
    const subreddits = new Set<string>();
    mentions.forEach((mention) => {
      if (mention.subreddit) {
        subreddits.add(mention.subreddit);
      }
    });
    return Array.from(subreddits).sort();
  }, [mentions]);

  /**
   * Convert date filter strings to Date objects for DatePickerInput
   */
  const dateRange = useMemo((): [Date | null, Date | null] | undefined => {
    const from = filters.dateFrom ? new Date(filters.dateFrom) : null;
    const to = filters.dateTo ? new Date(filters.dateTo) : null;
    // Return undefined if both are null (unset filter)
    if (!from && !to) {
      return undefined;
    }
    return [from, to];
  }, [filters.dateFrom, filters.dateTo]);

  /**
   * Check if all selectable mentions on current page are selected
   */
  const selectableMentions = useMemo(
    (): RedditMentionDto[] =>
      mentions.filter((m) => m.matchStatus !== 'Verified' && m.matchStatus !== 'Rejected'),
    [mentions]
  );

  const isAllSelected = useMemo(
    (): boolean => selectableMentions.length > 0 && selectedMentionIds.size === selectableMentions.length,
    [selectableMentions, selectedMentionIds]
  );

  const isIndeterminate = useMemo(
    (): boolean => selectedMentionIds.size > 0 && selectedMentionIds.size < selectableMentions.length,
    [selectableMentions, selectedMentionIds]
  );

  return (
    <Stack gap="lg">
      {/* Header - Different for business owners vs admins */}
      <Group justify="space-between">
        <Box>
          <Title order={1} ff="Comfortaa, sans-serif">
            {isAdmin ? 'Mentions' : `Welcome, ${businessName}`}
          </Title>
          <Text c="dimmed" size="lg" mt="xs">
            {isAdmin
              ? 'Review and manage online mentions of businesses'
              : 'Review and confirm mentions of your business'}
          </Text>
        </Box>

        <Button
          leftSection={<IconRefresh size={16} />}
          variant="light"
          onClick={handleRefresh}
          loading={isLoading}
        >
          Refresh
        </Button>
      </Group>

      {/* Sprint 037 Session 9: Status Banners for Business Owners */}
      {!isAdmin && (
        <>
          {/* Pending Business Setup Banner */}
          {pendingBusinessClaim && !user?.businessId && (
            <PendingBusinessSetupBanner
              businessName={pendingBusinessClaim.businessName}
              submittedAt={pendingBusinessClaim.submittedAt}
            />
          )}

          {/* Claim Status Banner */}
          {activeClaim && !pendingBusinessClaim && (
            <ClaimStatusBanner claim={activeClaim} />
          )}

          {/* Payment Required Banner */}
          {showPaymentRequired && (
            <PaymentRequiredBanner
              businessName={businessName}
              onCompletePayment={handleCompletePayment}
            />
          )}
        </>
      )}

      {/* Sprint 037 Session 9: Collapsible Stats for Business Owners */}
      {!isAdmin && stats && hasAccess && (
        <Paper shadow="sm" p="md" radius="md" withBorder>
          <Group justify="space-between" mb={showStats ? 'md' : 0}>
            <Group gap="xs">
              <IconTrendingUp size={20} />
              <Text fw={600}>Quick Stats</Text>
            </Group>
            <Button
              variant="subtle"
              size="xs"
              onClick={handleToggleStats}
              rightSection={showStats ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
            >
              {showStats ? 'Hide' : 'Show'}
            </Button>
          </Group>

          <Collapse in={showStats}>
            <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
              {/* Total Mentions */}
              <Paper p="sm" radius="md" style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
                <Group justify="space-between">
                  <Box>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                      Total
                    </Text>
                    <Text size="xl" fw={700}>
                      {stats.totalMentions}
                    </Text>
                  </Box>
                  <IconTrendingUp size={24} stroke={1.5} color="var(--mantine-color-blue-6)" />
                </Group>
              </Paper>

              {/* Pending Verification */}
              <Paper p="sm" radius="md" style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
                <Group justify="space-between">
                  <Box>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                      Pending
                    </Text>
                    <Text size="xl" fw={700}>
                      {stats.pendingVerification}
                    </Text>
                  </Box>
                  <IconClock size={24} stroke={1.5} color="var(--mantine-color-orange-6)" />
                </Group>
              </Paper>

              {/* Verified */}
              <Paper p="sm" radius="md" style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
                <Group justify="space-between">
                  <Box>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                      Verified
                    </Text>
                    <Text size="xl" fw={700}>
                      {stats.verifiedMentions}
                    </Text>
                  </Box>
                  <IconChecks size={24} stroke={1.5} color="var(--mantine-color-green-6)" />
                </Group>
              </Paper>

              {/* Average Confidence */}
              <Paper p="sm" radius="md" style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
                <Group justify="space-between">
                  <Box>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                      Avg Confidence
                    </Text>
                    <Text size="xl" fw={700}>
                      {stats.avgConfidence}%
                    </Text>
                  </Box>
                  <IconTrendingUp size={24} stroke={1.5} color="var(--mantine-color-violet-6)" />
                </Group>
              </Paper>
            </SimpleGrid>
          </Collapse>
        </Paper>
      )}

      {/* Sprint 037 Session 9: Limited Access State for Business Owners without Subscription */}
      {!isAdmin && !hasAccess && !isAccessLoading && user?.businessId && !pendingBusinessClaim && (
        <Paper shadow="sm" p="xl" radius="md" withBorder>
          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 16,
              padding: 32,
              background: 'linear-gradient(135deg, var(--mantine-color-gray-0) 0%, var(--mantine-color-gray-1) 100%)',
              borderRadius: 8
            }}
          >
            <ThemeIcon size={48} radius="xl" color="gray" variant="light">
              <IconLock size={24} />
            </ThemeIcon>
            <Text ta="center" fw={500} size="lg">
              Subscribe to View Mentions
            </Text>
            <Text ta="center" c="dimmed" size="sm" maw={400}>
              Complete your subscription to access real-time mentions of your business
              across Reddit and other platforms.
            </Text>
            <Button
              variant="filled"
              color="amber"
              leftSection={<IconArrowRight size={16} />}
              onClick={handleCompletePayment}
              mt="xs"
            >
              Complete Subscription
            </Button>
          </Box>
        </Paper>
      )}

      {/* Filters - SESSION_207: Improved styling with responsive Grid layout */}
      <Paper shadow="sm" p="md" radius="md" withBorder>
        <Stack gap="md">
          {/* Filter Header */}
          <Group justify="space-between">
            <Group gap="xs">
              <IconFilter size={20} />
              <Text fw={600} size="lg">Filters</Text>
            </Group>
            {(filters.status || filters.subreddit || filters.dateFrom || filters.dateTo) && (
              <Button
                variant="subtle"
                size="sm"
                leftSection={<IconFilterOff size={16} />}
                onClick={handleClearFilters}
                color="gray"
              >
                Clear All Filters
              </Button>
            )}
          </Group>

          {/* Filter Controls - Responsive Grid */}
          <Grid gutter="md">
            {/* Status Filter - SESSION_045: Updated to use ReviewStatus (manual admin review) */}
            <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
              <Select
                label="Status"
                placeholder="Filter by status"
                value={filters.status ?? 'All'}
                onChange={handleStatusChange}
                data={[
                  { value: 'All', label: 'All Mentions' },
                  { value: 'Pending', label: 'Pending Review' },
                  { value: 'Approved', label: 'Approved' },
                  { value: 'Rejected', label: 'Rejected' },
                  { value: 'Flagged', label: 'Flagged' }
                ]}
                clearable
              />
            </Grid.Col>

            {/* Subreddit Filter */}
            <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
              <Select
                label="Subreddit"
                placeholder="All subreddits"
                value={filters.subreddit ?? 'All'}
                onChange={handleSubredditChange}
                data={[
                  { value: 'All', label: 'All Subreddits' },
                  ...uniqueSubreddits.map((s) => ({ value: s, label: `r/${s}` }))
                ]}
                clearable
                searchable
              />
            </Grid.Col>

            {/* Date Range Filter */}
            <Grid.Col span={{ base: 12, sm: 12, md: 4 }}>
              <DatePickerInput
                type="range"
                label="Date Range"
                placeholder="Select date range"
                leftSection={<IconCalendar size={16} />}
                value={dateRange}
                onChange={handleDateRangeChange as (value: unknown) => void}
                clearable
              />
            </Grid.Col>
          </Grid>

          {/* Results Count */}
          <Text size="sm" c="dimmed">
            {totalCount} total mentions
          </Text>
        </Stack>
      </Paper>

      {/* Bulk Actions Bar - Shows when items are selected (Admin only) */}
      {isAdmin && selectedMentionIds.size > 0 && (
        <Paper p="md" withBorder bg="blue.0" radius="md">
          <Group justify="space-between">
            <Group>
              <Text fw={600} size="sm">
                {selectedMentionIds.size} mention{selectedMentionIds.size !== 1 ? 's' : ''} selected
              </Text>
              <Button
                variant="subtle"
                size="sm"
                onClick={handleClearSelection}
              >
                Clear Selection
              </Button>
            </Group>
            <Group>
              <Button
                color="green"
                size="sm"
                leftSection={<IconCheck size={16} />}
                onClick={handleOpenBulkConfirmModal}
              >
                Bulk Confirm
              </Button>
              <Button
                color="red"
                size="sm"
                leftSection={<IconX size={16} />}
                onClick={(): void => {
                  void handleBulkDismiss();
                }}
                loading={dismissMutation.isPending}
              >
                Bulk Dismiss
              </Button>
            </Group>
          </Group>
        </Paper>
      )}

      {/* Error Display */}
      {error && (
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
          {error instanceof Error ? error.message : 'Failed to load mentions'}
        </Alert>
      )}

      {/* Mentions Display - Cards for business owners, Table for admins */}
      {isLoading ? (
        <Paper shadow="sm" p="xl" radius="md" withBorder>
          <Group justify="center">
            <Loader size="lg" />
            <Text c="dimmed">Loading mentions...</Text>
          </Group>
        </Paper>
      ) : mentions.length > 0 ? (
        <>
          {/* SESSION_204: Card view for business owners */}
          {/* SESSION_209: Added showApprovalActions for business owner approval feature */}
          {!isAdmin ? (
            <SimpleGrid cols={{ base: 1 }} spacing="md">
              {mentions.map((mention: RedditMentionDto) => (
                <CustomerMentionCard
                  key={mention.id}
                  mention={mention}
                  onClick={handleViewDetails}
                  showApprovalActions
                />
              ))}
            </SimpleGrid>
          ) : (
            /* Admin Table View - Keep for bulk actions */
            <Paper shadow="sm" p="md" radius="md" withBorder>
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    {/* Checkbox Column (Admin only) */}
                    <Table.Th style={{ width: 40 }}>
                      <Checkbox
                        checked={isAllSelected}
                        indeterminate={isIndeterminate}
                        onChange={(event): void => handleSelectAll(event.currentTarget.checked)}
                        aria-label="Select all mentions"
                        disabled={selectableMentions.length === 0}
                      />
                    </Table.Th>
                    <Table.Th>Mention Text</Table.Th>
                    <Table.Th>Subreddit</Table.Th>
                    <Table.Th>Business</Table.Th>
                    <Table.Th>Confidence</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Date</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {mentions.map((mention: RedditMentionDto) => {
                    const isSelected = selectedMentionIds.has(mention.id);
                    const isSelectable = mention.matchStatus !== 'Verified' && mention.matchStatus !== 'Rejected';

                    return (
                      <Table.Tr
                        key={mention.id}
                        bg={isSelected ? 'blue.0' : undefined}
                      >
                        {/* Checkbox Column */}
                        <Table.Td>
                          <Checkbox
                            checked={isSelected}
                            onChange={(): void => handleSelectOne(mention.id)}
                            aria-label={`Select mention ${mention.id}`}
                            disabled={!isSelectable}
                          />
                        </Table.Td>

                        {/* Mention Text - Clickable link to Reddit (SESSION_206: added context=3) */}
                        <Table.Td>
                          <Anchor
                            href={appendRedditContext(mention.postPermalink)}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="sm"
                          >
                            <Text size="sm" lineClamp={2} maw={300}>
                              {mention.content || mention.postTitle || 'No content'}
                            </Text>
                          </Anchor>
                        </Table.Td>

                        {/* Subreddit */}
                        <Table.Td>
                          <SubredditBadge subreddit={mention.subreddit} />
                        </Table.Td>

                        {/* Business */}
                        <Table.Td>
                          <Text size="sm" c={mention.businessName ? undefined : 'dimmed'}>
                            {mention.businessName || 'Unmatched'}
                          </Text>
                        </Table.Td>

                        {/* Confidence Score */}
                        <Table.Td>
                          <ConfidenceIndicator score={mention.matchConfidenceScore ?? 0} />
                        </Table.Td>

                        {/* Status - SESSION_045: Display ReviewStatus (manual admin review) */}
                        <Table.Td>
                          <StatusBadge status={mention.reviewStatus as any} />
                        </Table.Td>

                        {/* Date */}
                        <Table.Td>
                          <Text size="sm" c="dimmed">
                            {formatDate(mention.createdAt)}
                          </Text>
                        </Table.Td>

                        {/* Actions */}
                        <Table.Td>
                          <Group gap="xs">
                            {/* View Details */}
                            <Tooltip label="View Details">
                              <ActionIcon
                                variant="light"
                                color="blue"
                                size="sm"
                                onClick={(): void => handleViewDetails(mention)}
                                aria-label="View mention details"
                              >
                                <IconEye size={16} />
                              </ActionIcon>
                            </Tooltip>

                            {/* Confirm - for pending mentions */}
                            {mention.matchStatus !== 'Verified' && mention.matchStatus !== 'Rejected' && (
                              <Tooltip label="Confirm">
                                <ActionIcon
                                  variant="light"
                                  color="green"
                                  size="sm"
                                  onClick={(): void => handleOpenConfirmModal(mention)}
                                  aria-label="Confirm this mention"
                                >
                                  <IconCheck size={16} />
                                </ActionIcon>
                              </Tooltip>
                            )}

                            {/* Dismiss - for non-rejected mentions */}
                            {mention.matchStatus !== 'Rejected' && (
                              <Tooltip label="Dismiss">
                                <ActionIcon
                                  variant="light"
                                  color="red"
                                  size="sm"
                                  loading={dismissMutation.isPending}
                                  onClick={(): void => {
                                    void handleDismiss(mention.id);
                                  }}
                                  aria-label="Dismiss this mention"
                                >
                                  <IconX size={16} />
                                </ActionIcon>
                              </Tooltip>
                            )}
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Paper>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Group justify="center" mt="lg">
              <Pagination
                value={filters.pageNumber}
                onChange={handlePageChange}
                total={totalPages}
                siblings={1}
                boundaries={1}
              />
            </Group>
          )}
        </>
      ) : (
        <Paper shadow="sm" p="xl" radius="md" withBorder>
          <Text c="dimmed" ta="center">
            No mentions found. Try adjusting your filters.
          </Text>
        </Paper>
      )}

      {/* Modals */}
      {selectedMention && (
        <>
          <MentionDetailModal
            mention={selectedMention}
            opened={detailModalOpen}
            onClose={handleCloseDetailModal}
          />

          <ConfirmMentionModal
            mention={selectedMention}
            opened={confirmModalOpen}
            onClose={handleCloseConfirmModal}
          />
        </>
      )}

      {/* Bulk Confirm Modal */}
      <BulkConfirmModal
        mentionIds={Array.from(selectedMentionIds)}
        opened={bulkConfirmModalOpen}
        onClose={handleCloseBulkConfirmModal}
      />
    </Stack>
  );
};
